import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  Alert,
  Box,
  Button,
  Chip,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import RateReviewIcon from "@mui/icons-material/RateReview";
import { API_URL } from "./config";
import { useNavigate } from "react-router-dom";

const StudentLessonListPage = () => {
  const token = localStorage.getItem("auth") || localStorage.getItem("token");
  const navigate = useNavigate();

  const THEME_ORANGE = "#F07828";
  const THEME_ORANGE_2 = "#ed7f35ff";
  const BG = "#F2F2F2";
  const BORDER = "rgba(25,118,210,0.20)";
  const ROW_ALT = "rgba(25,118,210,0.06)";
  const ROW_HOVER = "rgba(25,118,210,0.10)";

  const primaryBtnSx = {
    borderRadius: 2,
    textTransform: "none",
    fontWeight: 900,
    backgroundColor: THEME_ORANGE,
    "&:hover": { backgroundColor: THEME_ORANGE_2 },
  };

  const outlinedBtnSx = {
    borderRadius: 2,
    textTransform: "none",
    fontWeight: 900,
    borderColor: "rgba(25,118,210,0.35)",
    color: "rgba(25,118,210,0.95)",
    "&:hover": {
      borderColor: "rgba(25,118,210,0.55)",
      backgroundColor: "rgba(25,118,210,0.04)",
    },
  };

  const [rows, setRows] = useState([]);
  const [error, setError] = useState("");

  const openLesson = (lessonId) => {
    navigate(`/student/lessons/${lessonId}`);
  };

  const reviewLesson = (lessonId) => {
    navigate(`/student/lessons/${lessonId}?mode=review`);
  };

  const fetchLessons = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/ai-lesson-delivery/student/lessons`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setRows(res.data?.lessons || []);
    } catch (err) {
      setError(err.response?.data?.error || "Failed to fetch lessons");
    }
  };

  useEffect(() => {
    fetchLessons();
  }, []);

  return (
    <Box sx={{ p: 2, backgroundColor: BG, minHeight: "100vh" }}>
      <Paper
        elevation={5}
        sx={{
          borderRadius: 2,
          overflow: "hidden",
          border: "1px solid rgba(25,118,210,0.15)",
        }}
      >
        <Box
          sx={{
            px: 2,
            py: 1.5,
            background: `linear-gradient(90deg, ${THEME_ORANGE} 0%, ${THEME_ORANGE_2} 100%)`,
            color: "white",
          }}
        >
          <Typography sx={{ fontSize: "1.1rem", fontWeight: 900 }}>
            My Lessons
          </Typography>
          <Typography sx={{ opacity: 0.9, fontSize: "0.85rem" }}>
            Published lessons available for your enrolled classes
          </Typography>
        </Box>

        <Box sx={{ p: 2, backgroundColor: BG }}>
          {!!error && (
            <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
              {error}
            </Alert>
          )}

          <Paper elevation={0} sx={{ border: `1px solid ${BORDER}`, borderRadius: 2, overflow: "hidden" }}>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow
                    sx={{
                      backgroundColor: "rgba(25,118,210,0.95)",
                      "& th": { color: "#fff", fontWeight: 900 },
                    }}
                  >
                    <TableCell>Lesson</TableCell>
                    <TableCell>Subject</TableCell>
                    <TableCell>Class</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Score</TableCell>
                    <TableCell>Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((row, idx) => (
                    <TableRow
                      key={row.LessonId}
                      sx={{
                        backgroundColor: idx % 2 === 0 ? ROW_ALT : "#fff",
                        "&:hover": { backgroundColor: ROW_HOVER },
                      }}
                    >
                      <TableCell>
                        <Stack spacing={0.5}>
                          <Typography sx={{ fontWeight: 900 }}>{row.LessonTitle}</Typography>
                          <Typography variant="body2" color="text.secondary">
                            {row.ChapterTitle || "-"}
                          </Typography>
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Stack spacing={0.5}>
                          <Typography>{row.SubjectCode || "-"}</Typography>
                          <Typography variant="body2" color="text.secondary">
                            {row.SubjectName || "-"}
                          </Typography>
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={0.5} flexWrap="wrap">
                          <Chip size="small" label={`Branch ${row.BranchCode || "-"}`} />
                          <Chip size="small" label={`Sem ${row.Semester || "-"}`} />
                          <Chip size="small" label={`Sec ${row.Section || "-"}`} />
                        </Stack>
                      </TableCell>
                      <TableCell>{row.AttemptStatus || "NOT_STARTED"}</TableCell>
                      <TableCell>
                        {Number(row.TotalScore || 0)} / {Number(row.MaxScore || 0)}
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={1} flexWrap="wrap">
                          <Button
                            variant="contained"
                            sx={primaryBtnSx}
                            startIcon={<VisibilityIcon />}
                            onClick={() => openLesson(row.LessonId)}
                          >
                            {row.AttemptStatus === "SUBMITTED" || row.AttemptStatus === "GRADED" ? "View" : "Open"}
                          </Button>
                        
                          {(row.AttemptStatus === "SUBMITTED" || row.AttemptStatus === "GRADED") && (
                            <Button
                              variant="outlined"
                              sx={outlinedBtnSx}
                              startIcon={<RateReviewIcon />}
                              onClick={() => reviewLesson(row.LessonId)}
                            >
                              Review
                            </Button>
                          )}
                        </Stack>
                        {/*
                        <Button
                          variant="contained"
                          startIcon={<VisibilityIcon />}
                          sx={{
                            borderRadius: 2,
                            textTransform: "none",
                            fontWeight: 900,
                            backgroundColor: THEME_ORANGE,
                            "&:hover": { backgroundColor: THEME_ORANGE_2 },
                          }}
                          onClick={() => navigate(`/student/lessons/${row.LessonId}`)}
                        >
                          Open
                        </Button>
                        */}
                      </TableCell>
                    </TableRow>
                  ))}
                  {!rows.length && (
                    <TableRow>
                      <TableCell colSpan={6}>No lessons available.</TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Box>
      </Paper>
    </Box>
  );
};

export default StudentLessonListPage;